/**
 * Single packing-photo capture abstraction (§13.4, §44). Prefers
 * ImageCapture.takePhoto() (true still, capped ~1080–1440p — never 4K per §44),
 * falls back to grabFrame()/canvas.drawImage. Returns a JPEG Blob (~0.7 quality)
 * or null if no stream is available (capture is best-effort; must never block
 * the pack flow, §13.4).
 *
 * ImageCapture is not in the default DOM lib; typed locally rather than pulling
 * in extra @types. TODO ‹VERIFY›: confirm getPhotoCapabilities resolution caps
 * on the mandated Chromium kiosk webcam model in production.
 */

const MAX_DIMENSION = 1920; // 1080p class; do not target 4K (§44).

interface PhotoCapabilitiesLike {
  imageWidth?: { max?: number; min?: number };
  imageHeight?: { max?: number; min?: number };
}
interface ImageCaptureLike {
  takePhoto(settings?: { imageWidth?: number; imageHeight?: number }): Promise<Blob>;
  grabFrame(): Promise<ImageBitmap>;
  getPhotoCapabilities(): Promise<PhotoCapabilitiesLike>;
}
type ImageCaptureCtor = new (track: MediaStreamTrack) => ImageCaptureLike;

export async function capturePackingPhoto(video: HTMLVideoElement | null): Promise<Blob | null> {
  const stream = video?.srcObject;
  if (!(stream instanceof MediaStream)) return null;
  const track = stream.getVideoTracks()[0];
  if (!track) return null;

  // Preferred path: ImageCapture.takePhoto (§44). Some lib.dom versions ship a
  // built-in ImageCapture type that doesn't structurally match ours, so we go
  // through `unknown` to read it off globalThis without colliding.
  const Ctor = (globalThis as unknown as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
  if (Ctor) {
    try {
      const capture = new Ctor(track);
      let width: number | undefined;
      let height: number | undefined;
      try {
        const caps = await capture.getPhotoCapabilities();
        width = capImageDimension(caps.imageWidth?.max);
        height = capImageDimension(caps.imageHeight?.max);
      } catch {
        // capabilities unavailable — let takePhoto pick defaults.
      }
      const blob = await capture.takePhoto(
        width && height ? { imageWidth: width, imageHeight: height } : undefined,
      );
      return blob;
    } catch {
      // fall through to grabFrame/canvas.
    }
  }

  // Fallback: grab a frame and re-encode as JPEG (§44 defensive path).
  return drawFrameToJpeg(video);
}

function capImageDimension(max: number | undefined): number | undefined {
  if (!max) return undefined;
  return Math.min(max, MAX_DIMENSION);
}

async function drawFrameToJpeg(video: HTMLVideoElement | null): Promise<Blob | null> {
  if (!video) return null;
  const w = Math.min(video.videoWidth || MAX_DIMENSION, MAX_DIMENSION);
  const h = Math.min(video.videoHeight || Math.round((MAX_DIMENSION * 9) / 16), MAX_DIMENSION);
  if (w === 0 || h === 0) return null;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.7),
  );
}
